import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, DatePicker, Form, Modal, Switch } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import cx from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { FieldData } from 'rc-field-form/es/interface';
import { IAntDFormChangeFieldName, IApproveMonthlyOrder } from '../../../interfaces';
import { useMonthlyOrder } from '../../../hooks/stores/useMonthlyOrderStores';
import {
    apiMessagesDeleteMessageByFieldName,
    apiMessagesGetFormRules,
    apiMessagesGetFormRulesFieldBlank2,
    postMonthlyOrderApproveById,
} from '../../../middleware';
import { tB } from '../../../translate';
import BillingStyle from '../../BillingSystem/m_styles.less';
import Styles from '../../styles/m_itemCardStyles.less';

type IApproveMonthlyOrderModalForm = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
};

export const ApproveMonthlyOrderModalForm: React.FC<IApproveMonthlyOrderModalForm> = observer(
    ({ isOpen, setIsOpen }: IApproveMonthlyOrderModalForm) => {
        const [form] = Form.useForm();
        const [isValid, setIsValid] = useState(true);
        const [currentValues, setCurrentValues] = useState<IApproveMonthlyOrder>({});

        const { id } = useMonthlyOrder();
        const initialValues: IApproveMonthlyOrder = {
            dueDate: dayjs(),
            invoiceDate: dayjs(),
            sendInvoice: false,
            sendInvoiceOnDate: false,
        };

        const onFieldsChange = (changedFields: FieldData[]): void => {
            const fieldNameChange =
                changedFields && changedFields[0] && (changedFields[0].name as IAntDFormChangeFieldName);
            const fieldValueChange = changedFields && changedFields[0] && changedFields[0].value;
            switch (fieldNameChange[0]) {
                case 'sendInvoice':
                    if (!fieldValueChange) {
                        form.setFieldValue('sendInvoiceOnDate', false);
                    }
                    break;
                default:
                    break;
            }
            if (fieldNameChange) {
                apiMessagesDeleteMessageByFieldName(fieldNameChange);
            }
            setIsValid(!form.getFieldsError().filter(({ errors }) => errors.length).length);
            setCurrentValues(form.getFieldsValue());
        };

        const cancelApproveForm = (): void => {
            setIsOpen(false);
        };
        const submitApproveForm = async (): Promise<void> => {
            if (id) {
                const isSave = await postMonthlyOrderApproveById(id.toString(), currentValues);

                if (isSave) {
                    setIsOpen(false);
                } else {
                    form.validateFields();
                }
            }
        };

        const disabledDateInvoiceDate: RangePickerProps['disabledDate'] = (current: Dayjs) => {
            return current && current < dayjs();
        };
        const disabledDateDueDate: RangePickerProps['disabledDate'] = (current: Dayjs) => {
            return current && current < dayjs(currentValues.invoiceDate);
        };

        useEffect(() => {
            form.setFieldsValue({
                ...initialValues,
            });
            setCurrentValues(initialValues);
        }, [id]);

        useEffect(() => {
            return (): void => {
                setIsOpen(false);
            };
        }, []);

        return (
            <div>
                <Form
                    className={'formStyle'}
                    form={form}
                    initialValues={initialValues}
                    layout="vertical"
                    name="approveMonthlyOrder"
                    onFieldsChange={onFieldsChange}
                    onFinish={submitApproveForm}
                    scrollToFirstError={true}
                >
                    <Modal
                        title={'Approve Monthly Order'} // ToDo: 25.11.2025 -  need translate
                        wrapClassName={'modal'}
                        open={!!id && isOpen}
                        onCancel={cancelApproveForm}
                        footer={[
                            <Button
                                key="submit"
                                type="primary"
                                htmlType="submit"
                                onClick={submitApproveForm}
                                className={cx(Styles.mainButton, {
                                    [Styles.buttonsDisable]: !isValid,
                                })}
                                disabled={!id || !isValid}
                            >
                                {tB('Submit')}
                            </Button>,
                            <Button key="back" onClick={cancelApproveForm}>
                                {tB('Cancel')}
                            </Button>,
                        ]}
                    >
                        <Form.Item
                            required
                            key="invoiceDate"
                            name="invoiceDate"
                            label="Invoice Date"
                            rules={apiMessagesGetFormRulesFieldBlank2} // ToDo: 25.11.2025 - now not work show API error message for the invoiceDate field
                        >
                            {/*// ToDo: 25.11.2025 - need translate*/}
                            <DatePicker allowClear={false} disabledDate={disabledDateInvoiceDate} />
                        </Form.Item>
                        <Form.Item
                            required
                            key="dueDate"
                            name="dueDate"
                            label="Due Date"
                            rules={apiMessagesGetFormRulesFieldBlank2}
                        >
                            {/*// ToDo: 25.11.2025 - need translate*/}
                            <DatePicker allowClear={false} disabledDate={disabledDateDueDate} />
                        </Form.Item>
                        <Form.Item
                            key="sendInvoice"
                            name="sendInvoice"
                            valuePropName="checked"
                            rules={apiMessagesGetFormRules}
                        >
                            <Checkbox disabled={!Boolean(currentValues.invoiceDate)}>Send Invoice</Checkbox>
                        </Form.Item>
                        <Form.Item
                            key="sendInvoiceOnDate"
                            name="sendInvoiceOnDate"
                            valuePropName="checked"
                            rules={apiMessagesGetFormRules}
                        >
                            <div className={BillingStyle.newOrderDateField}>
                                <Switch
                                    unCheckedChildren={'Now'}
                                    checkedChildren={'On Date'}
                                    onChange={(v) => {
                                        setCurrentValues({ ...currentValues, sendInvoiceOnDate: v });
                                    }}
                                    checked={currentValues.sendInvoiceOnDate}
                                    disabled={!currentValues.sendInvoice || !currentValues.invoiceDate}
                                />
                                {Boolean(currentValues.sendInvoiceOnDate) && Boolean(currentValues.invoiceDate) ? (
                                    <div className={BillingStyle.newOrderDeducedInvoiceNumber}>
                                        {currentValues.invoiceDate?.format('YYYY-MM-DD')}
                                    </div>
                                ) : null}
                            </div>
                        </Form.Item>
                    </Modal>
                </Form>
            </div>
        );
    },
);
